import { inject, Injectable } from '@angular/core';
import {
  createAddress,
  ICustomerBankKonto,
  IERechnungsInfo,
  IGeschaeftsPartner,
  IOrderDetailsInfo,
  IPartner,
  IPersonalData,
  IPhysischePerson,
  IProduktDetails,
  IRelocationDataDTO,
  IRelocationDTOV2,
  IRelocationMappingData,
  IVertragskonto,
  newRelocationDTOV1,
  parseAddress,
  Product,
  toReferrencedFiles,
} from '@mwe/models';
import {
  decryptIBAN,
  encryptIBAN,
  getCategorySequence,
  getFromOlavKey,
  getProductFromDetailsInfo,
  getRelocationEmail,
  getRelocationFromDetail,
  isProductCategoryFernwaerme,
  isProductCategoryStromOrGas,
  sortProductsEnumCategory,
} from '@mwe/utils';
import { MSG_SUFFIX, TariffSelectionCardLogic, TariffSelectionStore } from '@mwe/ui/components';
import { TariffChangeService } from '../tariff/tariff-change.service';
import { AccountLogic } from '../account/account.logic';
import { RelocationStore } from './relocation.store';
import { TariffSelectionStateService } from '../tariff/selection/tariff-selection-state.service';
import { ProductCategoryEnum } from '@mwe/constants';

@Injectable({ providedIn: 'root' })
export class RelocationLogic {
  private readonly relocationStore = inject(RelocationStore);
  private readonly accountLogic = inject(AccountLogic);
  private readonly tariffSelectionStateService = inject(TariffSelectionStateService);
  private readonly tariffSelectionCardLogic = inject(TariffSelectionCardLogic);
  private readonly tariffChangeService = inject(TariffChangeService);
  private readonly tariffSelectionStore = inject(TariffSelectionStore);

  async initTariffSelection(): Promise<void> {
    // Reset Tariff Store to prevet update issues when changing products after Cards have been initialized.
    this.tariffSelectionCardLogic.reset();
    this.tariffSelectionCardLogic.initializeStoreData({
      allProducts: this.relocationStore.allSelectedProducts(),
      allPriceIndications: this.relocationStore.allPriceIndications(),
      zipCode: this.relocationStore.relocationToOlav().plz,
      userSelectedPriceIndications: this.relocationStore.selectedProductsPriceIndications(),
      userSelectedItems: this.relocationStore.selectedTariffs() ? this.relocationStore.selectedProducts() : [],
      defaultProducts: this.tariffChangeService.getConfigDefaultTariffs(),
      additionalOptionGroupName: 'Zusatzoptionen',
    });
    await this.tariffSelectionCardLogic.checkInitialVoucherCode();
  }

  async encryptDetails(details: IOrderDetailsInfo[]): Promise<IOrderDetailsInfo[]> {
    const hashCode = this.relocationStore.relocationFromAddress()?.hashCode;
    if (!hashCode)
      throw new Error(
        'Can not encrypt details because hashCode is not set. Please make sure that the address is loaded before encrypting the details.',
      );

    const encryptionTasks = details.map(async detail => {
      return {
        ...detail,
        produktDetails: {
          ...detail.produktDetails,
          vertragskonto: detail.produktDetails?.vertragskonto
            ? ((await encryptIBAN(detail.produktDetails.vertragskonto, hashCode)) as IVertragskonto)
            : detail.produktDetails?.vertragskonto,
        },
        verrechnungskonto: {
          ...detail.verrechnungskonto,
          eingangskonto: detail.verrechnungskonto?.eingangskonto
            ? ((await encryptIBAN(detail.verrechnungskonto.eingangskonto, hashCode)) as ICustomerBankKonto)
            : detail.verrechnungskonto?.eingangskonto,
          ausgangskonto: detail.verrechnungskonto?.ausgangskonto
            ? ((await encryptIBAN(detail.verrechnungskonto.ausgangskonto, hashCode)) as ICustomerBankKonto)
            : detail.verrechnungskonto?.ausgangskonto,
        },
      } as IOrderDetailsInfo;
    });

    return Promise.all(encryptionTasks);
  }

  isThereOnlyOneUniqueCustomer(products: Product[]): boolean {
    if (!Array.isArray(products) || products.length <= 1) {
      return true;
    }

    const customerNumbers = products.map(product => product.customerNumber);
    const uniqueCustomerNumbers = Array.from(new Set(customerNumbers));

    return uniqueCustomerNumbers.length === 1;
  }

  getUniqueCustomerNumbersWithStatus(): string[] {
    const customerAccountNumbers = this.relocationStore.uniqueCustomerNumbers();
    const moveOutAccounts: string[] = [];
    const newClientAccounts: string[] = [];
    const newClientGpIds: string[] = [];

    for (const caNumber of customerAccountNumbers) {
      const ids = caNumber.split('_');

      let details = this.relocationStore
        .relocationDetails()
        .filter(rd => rd.customerNumber === ids[0] && rd.accountNumber === ids[1] && rd.status === 'Abmeldung');
      if (details?.length > 0) {
        moveOutAccounts.push(caNumber + '_Abmeldung_' + details.map(d => d.category).join(','));
      }

      if (!newClientGpIds.includes(caNumber)) {
        newClientGpIds.push(caNumber);

        if (Number.isNaN(Number(ids[0]))) {
          details = this.relocationStore
            .relocationDetails()
            .filter(rd => (rd.customerNumber === null || Number.isNaN(Number(rd.customerNumber))) && rd.status === 'Anmeldung');
        } else if (Number.isNaN(Number(ids[1]))) {
          details = this.relocationStore.relocationDetails().filter(rd => rd.customerNumber === ids[0] && rd.status === 'Anmeldung');
        } else {
          details = this.relocationStore
            .relocationDetails()
            .filter(rd => rd.customerNumber === ids[0] && rd.accountNumber === ids[1] && rd.status === 'Anmeldung');
        }

        const newProducts = sortProductsEnumCategory(
          details.map(d => d.category),
          true,
        );

        for (const category of newProducts) {
          newClientAccounts.push(ids[0] + '_Anmeldung_' + category);
        }
      }
    }

    return [...this.sortMoveoutAccounts(moveOutAccounts), ...this.sortMoveoutAccounts(newClientAccounts)];
  }

  async getRelocationDTOV2(): Promise<IRelocationDTOV2> {
    const relocationDetails = this.relocationStore.relocationDetails();
    const abmeldungRelocationDetails = relocationDetails.filter(orderDetails => orderDetails.status === 'Abmeldung');
    const anmeldungRelocationDetails = relocationDetails.filter(orderDetails => orderDetails.status === 'Anmeldung');

    const mappingDataIds = this.getUniqueCustomerNumbersWithStatus();
    const hashCode = this.relocationStore.relocationFromAddress()?.hashCode;

    const firstOldIBAN = abmeldungRelocationDetails.find(orderDetails => orderDetails.bezahlartIcon === 'sepa')?.verrechnungskonto
      ?.eingangskonto?.iban;
    const firstNewIBAN = anmeldungRelocationDetails.find(orderDetails => orderDetails.bezahlartIcon === 'sepa')?.verrechnungskonto
      ?.eingangskonto?.iban;

    const eRechnungsInfo = this.relocationStore.eRechnungsInfo()?.isERechnungsEmpfaenger ? this.relocationStore.eRechnungsInfo() : null;
    const differentInvoiceRecipient = this.relocationStore.differentInvoiceRecipient();
    const firstOldERechnungEmail = abmeldungRelocationDetails.find(orderDetails => orderDetails.eRechnungInfo)?.eRechnungInfo
      ?.rechnungEmail;
    const email = this.getEmail(anmeldungRelocationDetails, eRechnungsInfo);
    // needed for old account i.e. abmeldung
    const changeERechnung = !!eRechnungsInfo?.isERechnungsEmpfaenger && firstOldERechnungEmail !== email;
    const changeSepaMandat = await this.hasSepaMandatChanged(firstOldIBAN, firstNewIBAN, hashCode);
    const mappingData: IRelocationMappingData[] = mappingDataIds.map((dataIds, orderIdx): IRelocationMappingData => {
      let moveOutInfo: IOrderDetailsInfo = null;
      let newClientInfo: IOrderDetailsInfo = null;

      const ids = dataIds.split('_');
      const categories = ids.at(-1).split(',');
      const customerNumber = Number.isNaN(Number(ids[0])) ? null : ids[0];

      if (dataIds.includes('Abmeldung')) {
        moveOutInfo = abmeldungRelocationDetails.find(
          orderDetails => orderDetails.customerNumber === ids[0] && orderDetails.accountNumber === ids[1],
        );
      }

      if (dataIds.includes('Anmeldung')) {
        newClientInfo = anmeldungRelocationDetails.find(orderDetails => orderDetails.category === categories[0]);
      }

      const hasValidVoucher = newClientInfo
        ? isProductCategoryStromOrGas(newClientInfo.category) &&
          this.relocationStore.voucherInfo()?.infoTextKey?.includes(newClientInfo.category + MSG_SUFFIX)
        : undefined;

      return {
        orderIdx,
        customerNumber,
        accountNumber: moveOutInfo ? ids[1] : null,
        businessPartnerNumber: customerNumber,
        categories,
        isuTariffKeys: [newClientInfo?.tariff.shortcut || moveOutInfo?.tariff.name],
        tariffKeys: [newClientInfo?.tariff.key],
        tariffName: newClientInfo?.tariff.name,
        tariffClasses: moveOutInfo ? [moveOutInfo.availabilityDetailsFernwaerme] : [this.getAvailabilitiesForFWProduct(newClientInfo)],
        tarifOptionen: newClientInfo?.tarifOptionen,
        condition: newClientInfo?.condition,
        zustelladresse: newClientInfo?.zustelladresse,
        personalData: this.getPersonalData(moveOutInfo, newClientInfo, email),
        invoiceAddress: parseAddress(newClientInfo?.invoiceAddress || this.relocationStore.relocationToAddress()),
        changeERechnung: moveOutInfo ? changeERechnung : !!eRechnungsInfo?.isERechnungsEmpfaenger, // for anmeldung set true if erechnung is true
        changeSepaMandat: moveOutInfo ? changeSepaMandat : firstNewIBAN?.length > 2, // for anmeldung set true if valid iban exists
        processAdvice: ids.at(-2),
        validVoucherAnlageId: hasValidVoucher ? '0' : null,
      };
    });

    return {
      oldAddress: this.relocationStore.relocationFromAddress(),
      newAddressOlav: this.relocationStore.relocationToOlav(),
      deregistrationDate: this.relocationStore.deregistrationDate(),
      registrationDate: this.relocationStore.registrationDate(),
      idCard: this.relocationStore.idCard(),
      referencedFiles: this.relocationStore.uploadedFiles() ? toReferrencedFiles(this.relocationStore.uploadedFiles().uploadedFiles) : null,
      pdfLinks: this.relocationStore.pdfLinks(),
      priceIndications: this.relocationStore.selectedProductsPriceIndications(),
      mappingData,
      eRechnungsInfo,
      sepaMandat: firstNewIBAN ? { iban: firstNewIBAN } : null,
      noOnlineCategories: this.relocationStore.noOnlineCategories(),
      noLongerAvailableCategories: this.relocationStore.noLongerAvailableCategories(),
      version: 'V2',
      selectedTariffs: this.tariffSelectionStore.selectedTariffs(),
      voucherInfo: this.relocationStore.voucherInfo(),
      priceSheets: this.tariffSelectionStateService.priceSheets, //TODO: use store
      differentInvoiceRecipient,
    };
  }

  getProductsForNoOnlineCategories(orderDetails: IOrderDetailsInfo[], noOnlineCategories: string[]): Product[] {
    const noOnlineProducts: Partial<Product>[] = [];

    if (noOnlineCategories) {
      for (const od of orderDetails) {
        if (noOnlineCategories.includes(od.category) && od.status === 'Abmeldung') {
          noOnlineProducts.push(getProductFromDetailsInfo(od));
        }
      }
    }

    return noOnlineProducts as Product[];
  }

  parseVersionTwo(relocationData: any, relocationDetails: any): IRelocationDataDTO {
    const dto: IRelocationDataDTO = {
      selectedOlav: relocationData?.newAddressOlav,
      orderDetailsInfo: undefined, // set in createOrderDetailsInfo
      oldAddress: createAddress(relocationData?.oldAddress),
      newAddress: getFromOlavKey(relocationData?.newAddressOlav),
      deregistrationDate: relocationData?.deregistrationDate,
      registrationDate: relocationData?.registrationDate,
      relocationSubmittedAt: relocationDetails?.['createdAt'], // TODO: This property doesn't exist?
      idcard: relocationData?.idCard,
      referencedFiles: relocationData?.referencedFiles,
      useSubstituteDetails: relocationData?.['useSubstituteDetails'], // TODO: This property doesn't exist?
      pdfLinks: relocationData?.pdfLinks,
      noOnlineCategories: relocationData?.noOnlineCategories,
      noLongerAvailableCategories: relocationData?.noLongerAvailableCategories,
      version: relocationDetails.version,
      selectedTariffs: relocationData?.selectedTariffs ?? [],
      priceSheets: relocationData.priceSheets,
      eRechnungsInfo: relocationData.eRechnungsInfo,
      eRechnungChangeIndices: undefined, // set in createOrderDetailsInfo
      sepaChangeIndices: undefined, // set in createOrderDetailsInfo
      voucherInfo: relocationData.voucherInfo,
      differentInvoiceRecipient: relocationData.differentInvoiceRecipient,
    };

    this.createOrderDetailsInfo(relocationData, dto);
    dto.oldAddress.addressGroup.id = relocationDetails.addressGroupId?.toString();

    return dto;
  }

  parseVersionOne(relocationData: any, relocationDetails: any): IRelocationDataDTO {
    const dto: IRelocationDataDTO = {
      selectedOlav: relocationData?.selectedOlav,
      oldAddress: createAddress(relocationData?.moveFromAddress ? relocationData?.moveFromAddress : relocationData?.oldAddress),
      newAddress: createAddress(relocationData?.moveToAddress ? relocationData?.moveToAddress : relocationData?.newAddress),
      deregistrationDate: relocationData?.deregistrationAt ? relocationData?.deregistrationAt : relocationData?.deregistrationDate,
      registrationDate: relocationData?.registrationAt ? relocationData?.registrationAt : relocationData?.registrationDate,
      relocationSubmittedAt: relocationDetails?.createdAt,
      orderDetailsInfo: relocationData?.relocationDetailsInfo ? relocationData?.relocationDetailsInfo : relocationData?.orderDetailsInfo,
      idcard: relocationData?.idcard,
      referencedFiles: relocationData?.referencedFiles,
      useSubstituteDetails: relocationData?.useSubstituteDetails,
      pdfLinks: relocationData?.pdfLinks,
      noOnlineCategories: relocationData?.noOnlineCategories,
      priceSheets: relocationData.priceSheets,
    };

    dto.oldAddress.addressGroup.id = relocationDetails.addressGroupId?.toString();

    return dto;
  }

  private sortMoveoutAccounts(moveOutAccounts: string[]): string[] {
    return moveOutAccounts.toSorted((a, b) => (this.getMoveOutCategory(a) < this.getMoveOutCategory(b) ? -1 : 1));
  }

  private getMoveOutCategory(moveOutAccount: string): number {
    const tokens = moveOutAccount.split('_');
    return getCategorySequence(tokens[tokens.length - 1].split(',')[0]);
  }

  private async hasSepaMandatChanged(oldIBAN: string, newIBAN: string, hashCode: string): Promise<boolean> {
    const oldBankDetails = await decryptIBAN({ iban: oldIBAN }, hashCode);
    const newBankDetails = await decryptIBAN({ iban: newIBAN }, hashCode);

    return oldBankDetails.iban !== newBankDetails.iban;
  }

  private getAvailabilitiesForFWProduct(newClientInfo: IOrderDetailsInfo): string[] {
    if (isProductCategoryFernwaerme(newClientInfo?.category)) {
      const fwDetails = this.relocationStore.relocationDetails().find(rd => rd.category === newClientInfo.category);
      return fwDetails?.availabilityDetailsFernwaerme;
    }

    return null;
  }

  public getEmail(anmeldungRelocationDetails: IOrderDetailsInfo[], eRechnungsInfo: IERechnungsInfo): string {
    // same logic as in RelocationSummaryComponent
    const fromDetail = getRelocationFromDetail(
      newRelocationDTOV1(this.relocationStore.relocationData()).orderDetailsInfo,
      anmeldungRelocationDetails.map(elem => ProductCategoryEnum[elem.category.toUpperCase()]),
    );

    return getRelocationEmail(fromDetail, eRechnungsInfo, this.accountLogic.getAccountEmail());
  }

  private getPersonalData(moveOutInfo: IOrderDetailsInfo, newClientInfo: IOrderDetailsInfo, email: string): IPersonalData {
    const partner = moveOutInfo?.produktDetails.partner || newClientInfo?.produktDetails.partner;
    const birthday = moveOutInfo?.physischePerson?.geburtsdatum || newClientInfo?.physischePerson?.geburtsdatum;

    if (partner) {
      return {
        salutation: partner.anrede,
        title: { text: partner?.titel, value: '' },
        firstName: partner.vorname,
        lastName: partner.nachname,
        birthday: birthday?.replace('T00:00Z-', ''),
        telefonNumber: partner.telefon,
        telefonCountryCode: partner.telefonvorwahl,
        email: partner.email || email,
      };
    }

    return null;
  }

  private createOrderDetailsInfo(relocationData: any, dto: IRelocationDataDTO): void {
    const orderDetailsInfo: IOrderDetailsInfo[] = [];
    const eRechnungChangeIndices: number[] = [];
    const sepaChangeIndices: number[] = [];
    const mappingData: IRelocationMappingData[] = relocationData.mappingData;

    for (const md of mappingData) {
      for (const category of md.categories) {
        if (md.processAdvice === 'Abmeldung') {
          if (md.changeERechnung) {
            eRechnungChangeIndices.push(md.orderIdx);
          }
          if (md.changeSepaMandat) {
            sepaChangeIndices.push(md.orderIdx);
          }
        }

        const info: IOrderDetailsInfo = {
          status: md.processAdvice as 'Abmeldung' | 'Anmeldung' | 'Tarifwechsel' | 'Bankverbindung',
          accountNumber: md.accountNumber,
          customerNumber: md.customerNumber,
          category: category,
          apiVersion: md['version'], // TODO: This property doesn't exist?
          tariff: md.isuTariffKeys?.[0]
            ? {
                shortcut: md.isuTariffKeys[0],
                key: md.tariffKeys[0],
                name: md.tariffName,
              }
            : null,
          tarifOptionen: md.tarifOptionen,
          availabilityDetailsFernwaerme: md.tariffClasses?.[0],
          eRechnungInfo:
            relocationData.eRechnungsInfo?.isERechnungsEmpfaenger === true
              ? relocationData.eRechnungsInfo
              : { isERechnungsEmpfaenger: false },
          bezahlartIcon: relocationData.sepaMandat ? 'sepa' : 'zahlschein',
          bezahlartText: relocationData.sepaMandat?.iban || 'zahlschein',
          partnerDetails: this.getPartnerDetails(md) as IGeschaeftsPartner,
          produktDetails: this.getProductDetails(md) as IProduktDetails,
          condition: md.condition,
          zustelladresse: md.zustelladresse,
          physischePerson: {
            geburtsdatum: md.personalData?.birthday,
            anrede: md.personalData?.salutation,
            vorname: md.personalData?.firstName,
            nachname: md.personalData?.lastName,
          },
        };

        orderDetailsInfo.push(info);
      }
    }

    dto.eRechnungChangeIndices = eRechnungChangeIndices.sort();
    dto.sepaChangeIndices = sepaChangeIndices.sort();
    dto.orderDetailsInfo = orderDetailsInfo;
  }

  private getProductDetails(md: IRelocationMappingData): Partial<IProduktDetails> {
    return {
      partner: {
        partnerId: md.customerNumber,
        anrede: md.personalData?.salutation,
        vorname: md.personalData?.firstName,
        nachname: md.personalData?.lastName,
        email: md.personalData?.email,
        telefon: md.personalData?.telefonNumber,
        telefonvorwahl: md.personalData?.telefonCountryCode,
      } as IPartner,
    };
  }

  private getPartnerDetails(md: IRelocationMappingData): Partial<IGeschaeftsPartner> {
    return {
      geburtsDatum: md.personalData?.birthday,
      physischePerson: {
        anrede: md.personalData?.salutation,
        vorname: md.personalData?.firstName,
        nachname: md.personalData?.lastName,
        email: md.personalData?.email, // TODO: This property doesn't exist?
        telefon: md.personalData?.telefonNumber,
        telefonvorwahl: md.personalData?.telefonCountryCode,
      } as IPhysischePerson,
    };
  }
}
